import { CalendarEvent } from 'angular-calendar';
import { take } from 'rxjs';

import { Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { AppRoutes } from '@app/app.routes';
import { IClientProperty } from '@clients/models/firebase/client.model';
import { JobCardOffcanvasComponent } from '@job-card/components/job-card-offcanvas/job-card-offcanvas.component';
import { ClientLeadsRoutes } from '@leads/client-facing/client-facing.routes';
import { leadAssessmentStatusOptions } from '@leads/client-facing/models/domain/scheduled-assessment.domain';
import { LeadRequestType } from '@leads/shared/models/domain/lead-request.domain';
import { ScheduledAssessmentStatus, ScheduledAssessmentStatusLabels } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { LeadAssessmentService } from '@leads/shared/services/lead-assessment/lead-assessment.service';
import { ScheduledEvent } from '@schedule/models/calendar/calendar.model';
import { CalendarService } from '@schedule/services/calendar/calendar.service';
import { convertTimestampToFormattedString } from '@shared/components/calendar/utilities';
import { IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';

import { CalendarColours, CalendarEventColours } from '../classes/event-colours';

export interface ICalendarEventOffcanvas {
	title: {
		iconColour: CalendarColours;
		text: string;
		subtitle?: string;
	};
	detailItems: Array<ICalendarEventOffcanvasItem>;
	customAction: CustomActions;
	buttons: Array<ICalendarEventOffcanvasButton>;
	alerts: Array<string>;
}

export interface ICalendarEventOffcanvasItem {
	visible: boolean;
	icon?: string;
	content?: {
		title: string;
		description?: string;
		location?: IClientProperty;
	};
	action?: {
		actionText: string;
		icon?: string;
		onClick: () => void;
	};
}

export type CustomActions = CalendarEventOffcanvasScheduledAssessmentDropdown;

export enum CalendarCustomActionType {
	ASSESSMENT_STATUS_DROPDOWN = 'ASSESSMENT_STATUS_DROPDOWN',
}

export interface ICalendarEventOffcanvasCustomAction<T> {
	type: CalendarCustomActionType;
	visible: boolean;
	canEdit: boolean;
	value: T;
	onChange: (value: T) => void;
	meta?: unknown;
}

export class CalendarEventOffcanvasScheduledAssessmentDropdown implements ICalendarEventOffcanvasCustomAction<ScheduledAssessmentStatus> {
	type = CalendarCustomActionType.ASSESSMENT_STATUS_DROPDOWN;
	visible: boolean;
	canEdit: boolean;
	value: ScheduledAssessmentStatus;
	meta: {
		options: Array<IDropdownOption>;
	};

	event: CalendarEvent;

	constructor(
		private calendarService: CalendarService,
		event: CalendarEvent,
		isOwner: boolean,
	) {
		this.visible = true;
		this.canEdit = isOwner;
		this.value = event.meta!.scheduledAssessmentStatus;
		this.meta = {
			options: leadAssessmentStatusOptions,
		};
		this.event = event;
	}

	onChange(status: ScheduledAssessmentStatus): void {
		this.calendarService.updateScheduledAssessmentStatus(this.event.meta!, status);
	}
}

export interface ICalendarEventOffcanvasButton {
	visible: boolean;
	disabled?: boolean;
	classColour: 'btn-primary' | 'btn-secondary' | 'btn-danger' | 'btn-success' | 'btn-warning' | 'btn-info' | 'btn-light' | 'btn-dark';
	text: string;
	isLoading?: boolean;
	icon?: string;
	onClick: () => void;
}

abstract class CalendarEventOffcanvas<T> {
	private _event: CalendarEvent<T>;
	protected details: Array<ICalendarEventOffcanvasItem> = [];
	protected buttons: Array<ICalendarEventOffcanvasButton> = [];
	protected alerts: Array<string> = [];
	protected meta: T;

	constructor(event: CalendarEvent) {
		this._event = event;
		this.meta = event.meta!;
	}

	get event(): CalendarEvent<T> {
		return this._event;
	}

	set event(e: CalendarEvent<T>) {
		this._event = e;
		this.meta = e.meta!;
	}

	abstract calendarEventOffcanvas(): ICalendarEventOffcanvas;
	abstract buildDetails(): void;
	abstract buildButtons(): void;
	abstract buildAlerts(): void;

	protected addDetailItem(detailItem: ICalendarEventOffcanvasItem): void {
		this.details.push(detailItem);
	}

	protected addButton(button: ICalendarEventOffcanvasButton): void {
		this.buttons.push(button);
	}

	protected addAlert(message: string): void {
		this.alerts.push(message);
	}
}

export class ScheduledEventCalendarEventOffcanvas extends CalendarEventOffcanvas<ScheduledEvent> {
	isOwner: boolean;
	constructor(
		private router: Router,
		private calendarService: CalendarService,
		private offcanvasService: OffcanvasService,
		private leadAssessmentService: LeadAssessmentService,
		private modalService: ModalService,
		event: CalendarEvent<ScheduledEvent>,
		isOwner: boolean,
	) {
		super(event);
		this.isOwner = isOwner;
	}

	calendarEventOffcanvas(): ICalendarEventOffcanvas {
		this.buildDetails();
		this.buildButtons();
		this.buildAlerts();

		return {
			title: {
				iconColour: CalendarEventColours.scheduledAssessment(this.event.meta?.scheduledAssessmentStatus!).secondary,
				text: this.event.title,
				subtitle: this.dateTimeRange(),
			},
			detailItems: this.details,
			customAction: new CalendarEventOffcanvasScheduledAssessmentDropdown(this.calendarService, this.event, this.isOwner),
			buttons: this.buttons,
			alerts: this.alerts,
		};
	}

	buildDetails(): void {
		this.details = [];
		this.addDetailItem({
			visible: this.isOwner,
			action: {
				actionText: 'View Lead',
				icon: 'bi-eye',
				onClick: () => this.viewLead(),
			},
		});
		this.addDetailItem({
			visible: !this.isOwner,
			icon: 'bi-info-circle-fill',
			content: {
				title: 'Status',
				description: ScheduledAssessmentStatusLabels[this.meta.scheduledAssessmentStatus],
			},
		});
		this.addDetailItem({
			visible: true,
			icon: 'bi-card-checklist',
			content: {
				title: 'Job Description',
				description: this.meta.jobDescription || 'Not available',
			},
		});
		this.addDetailItem({
			visible: true,
			icon: 'bi-house-door-fill',
			content: {
				title: 'Service',
				description: this.leadRequestTypeDescription(),
			},
		});
		this.addDetailItem({
			visible: !!this.meta.customerName,
			icon: 'bi-person-fill',
			content: {
				title: 'Customer',
				description: this.meta.customerName,
			},
		});
		this.addDetailItem({
			visible: !!this.meta.customerNumber,
			icon: 'bi-telephone-fill',
			content: {
				title: 'Contact Number',
				description: this.meta.customerNumber,
			},
		});
		this.addDetailItem({
			visible: !!this.meta.customerProperty,
			icon: 'bi-geo-alt-fill',
			content: {
				title: 'Location',
				location: this.meta.customerProperty,
			},
		});
		this.addDetailItem({
			visible: !!this.meta.employeeName,
			icon: 'bi-person-fill-gear',
			content: {
				title: 'Employee',
				description: this.meta.employeeName,
			},
		});
		this.addDetailItem({
			visible: !!this.meta.serviceTypes.length,
			icon: 'bi-gear-fill',
			content: {
				title: 'Service Types',
				description: this.meta.serviceTypes.length ? this.meta.serviceTypes.join(', ') : 'Not available',
			},
		});
		this.addDetailItem({
			visible: true,
			icon: 'bi-list-nested',
			content: {
				title: 'Internal Additional Notes',
				description: this.meta.internalAssessmentNotes || 'Not available',
			},
		});
	}

	buildButtons(): void {
		this.buttons = [];
		this.addButton({
			visible: !this.meta.jobCard?.startTime,
			disabled: !!this.meta.jobCard?.startTime,
			classColour: 'btn-success',
			text: 'Start Job',
			onClick: () => this.startJob(),
		});
		this.addButton({
			visible: !!this.meta.jobCard?.startTime && !this.meta.jobCard?.endTime,
			disabled: !!this.meta.jobCard?.endTime,
			classColour: 'btn-danger',
			text: 'End Job',
			onClick: () => this.endJob(),
		});
		this.addButton({
			visible: true,
			disabled: false,
			classColour: 'btn-primary',
			text: 'Job Card',
			onClick: () => this.viewJobCard(),
		});
	}

	buildAlerts(): void {
		this.alerts = [];
		if (!!this.meta.jobCard?.startTime && !this.meta.jobCard?.endTime) {
			this.alerts.push(
				`You have successfully started your job on ${convertTimestampToFormattedString(this.meta.jobCard.startTime)}. This will be recorded in the job card.`,
			);
		}

		if (!!this.meta.jobCard?.endTime && !!this.meta.jobCard?.startTime) {
			this.alerts.push(
				`You have successfully ended your job on ${convertTimestampToFormattedString(this.meta.jobCard.endTime)}. This will be recorded in the job card.`,
			);
		}
	}

	private dateTimeRange(): string {
		return Intl.DateTimeFormat('en-ZA', {
			weekday: 'short',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		}).formatRange(this.event.start, this.event.end!);
	}

	private leadRequestTypeDescription(): string {
		switch (this.event.meta?.leadRequestType) {
			case LeadRequestType.CALL:
				return 'Call Assessment';
			case LeadRequestType.ON_SITE_VISIT:
				return 'On Site Assessment';
			default:
				return 'Unknown Assessment';
		}
	}

	private viewLead(): void {
		this.router.navigate([
			`${AppRoutes.CLIENT_LEADS_DASHBOARD}/${ClientLeadsRoutes.ASSESSMENT_REVIEW.replace(':leadRequestId', this.event.meta!.leadRequestId)}`,
		]);
	}

	private startJob(): void {
		const startJobButton = this.buttons[0];
		startJobButton.isLoading = true;
		this.leadAssessmentService
			.updateScheduledAssessmentJobStartTime(this.event.meta?.scheduledAssessmentId!, Timestamp.now())
			.finally(() => (startJobButton.isLoading = false));
	}

	private endJob(): void {
		const endJobButton = this.buttons[1];
		endJobButton.isLoading = true;
		this.leadAssessmentService
			.updateScheduledAssessmentJobEndTime(this.event.meta?.scheduledAssessmentId!, Timestamp.now())
			.finally(() => (endJobButton.isLoading = false));
	}

	private viewJobCard(): void {
		const jobCardButton = this.buttons[2];
		jobCardButton.isLoading = true;

		const shouldClose = (): Promise<boolean> => {
			return new Promise((resolve) => {
				if (componentInstance.form.dirty) {
					const cm = this.modalService.open(ConfirmationModalComponent, false, 'md');
					const cmComponentInstance = cm.componentInstance as ConfirmationModalComponent;
					cmComponentInstance.title = 'Unsaved Changes - Do You Want to Discard?';
					cmComponentInstance.contentText = `You have unsaved changes in your job card. If you confirm, all changes will be lost. To continue editing or save your changes, click Back.`;
					cmComponentInstance.confirmAction.pipe(take(1)).subscribe(() => resolve(true));
				} else {
					resolve(true);
				}
			});
		};

		const ref = this.offcanvasService.open(JobCardOffcanvasComponent, 'end', true, 'oc-lg', shouldClose);
		const componentInstance = ref.componentInstance as JobCardOffcanvasComponent;
		componentInstance.leadAssessmentId = this.event.meta?.scheduledAssessmentId!;
		componentInstance.isOwner = this.isOwner;

		ref.shown.subscribe(() => {
			jobCardButton.isLoading = false;
		});

		ref.dismissed.subscribe(() => {
			jobCardButton.isLoading = false;
		});
	}
}
